/**
 * Public-visibility predicate for coach profiles.
 *
 * A coach is hidden from every public surface — direct profile URLs,
 * book/contact/offer pages, the discovery presentation, and the OG social
 * image all return 404 — when any of these hold:
 *
 * - the profile is an UNCLAIMED pre-generated one (`isPreGeneratedProfile`,
 *   issue #1014). The rows stay in the database: claiming the profile via
 *   `/api/ghl-create-account` flips the flag to `false`, which makes the
 *   profile publicly visible again.
 * - the coach switched their profile off (`isPublic === false`, issue #1013).
 *   The marketplace settings toggle promises the profile stops appearing on
 *   Dream Coach Match, so a link-holder must not get the page either. Account
 *   deletion sets the same flag (issue #1112), keeping the coach row for the
 *   clients' engagement history.
 * - there is no profile row at all — a coach with no profile has no public
 *   content, and `isPublic` cannot be true without the row that stores it.
 *
 * This is a pure data predicate; the owning coach's preview bypass lives in
 * `isCoachHiddenFromRequest` (`~/lib/server/coach-visibility`), which is what
 * public surfaces should call.
 *
 * Aliases: isCoachPubliclyHidden, coachHiddenReason, hidden coach predicate,
 * pre-generated profile gate, pregen profile visibility, private profile gate,
 * is_public 404
 */

/** Why a coach is hidden — `null` when the profile is publicly visible. */
export type CoachHiddenReason = "no_profile" | "private" | "pre_generated";

interface CoachVisibilityProfile {
  isPreGeneratedProfile: boolean;
  isPublic: boolean;
}

/**
 * The reason a coach is publicly hidden, or `null` when they are visible.
 *
 * The server gate needs the reason, not just the boolean: the owner-preview
 * bypass is only as trustworthy as the identity behind it, and `private`
 * (#1013) is the one reason where a weak identity is not good enough — see
 * `isCoachHiddenFromRequest`. `private` is therefore reported ahead of
 * `pre_generated`, so a profile that is both gets the stricter treatment.
 */
export function coachHiddenReason(
  profile: CoachVisibilityProfile | null | undefined,
): CoachHiddenReason | null {
  if (!profile) return "no_profile";
  if (!profile.isPublic) return "private";
  if (profile.isPreGeneratedProfile) return "pre_generated";
  return null;
}

export function isCoachPubliclyHidden(
  profile: CoachVisibilityProfile | null | undefined,
): boolean {
  return coachHiddenReason(profile) !== null;
}
